import React, { useState, useEffect, useRef, useMemo } from "react";
import { BarChart, Bar, XAxis, Tooltip } from "recharts";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs"; // Make sure you have this installed
import isBetween from "dayjs/plugin/isBetween";
import CloseIcon from "@mui/icons-material/Close";
import { fetchAllProjects } from "../../../store/slices/projectsSlice";
import {
  fetchWorkEntries,
  fetchWorkEntriesProjectTracking,
} from "../../../store/slices/workentriesSlice";
import { fetchEmployees } from "../../../store/slices/employeeSlice";
import {
  setmytrainingOMProjectAssignedState,
  setmytrainingOMProjectRequestState,
} from "../../../store/slices/OperationSlices/operationStateSlice";
import { useLoggedUser } from "../../../utils/useLoggedUser";
import { hasViewOrFullAccess, getFeatureAccessLevel } from "../../../utils/featureAccessUtils";

dayjs.extend(isBetween);
const formatHours = (value) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number(value) || 0);

const ManagerMyProjects = () => {
  const popupRef = useRef(null);
  const dispatch = useDispatch();
  const loggedUser = useLoggedUser();

  const hasNoFeatureConfig = !loggedUser?.featureAccess || loggedUser.featureAccess.length === 0;
  const canShowCard = (featureName) => {
    if (hasNoFeatureConfig) return true;
    if (hasViewOrFullAccess(loggedUser, featureName)) return true;
    if (!getFeatureAccessLevel(loggedUser, featureName)) {
      return hasViewOrFullAccess(loggedUser, "MY MANAGEMENT") ||
             hasViewOrFullAccess(loggedUser, "MY ASSIGNEMENT") ||
             hasViewOrFullAccess(loggedUser, "MY ASSIGNMENT");
    }
    return false;
  };

  const ongoingWorkentries = useSelector(
    (state) => state.operationFeature.ongoingWorkentries
  );
  const ongoingProjectsRedux = useSelector(
    (state) => state.operationFeature.ongoingProjectsRedux
  );
  const ongoingHardwareParts = useSelector(
    (state) => state.operationFeature.ongoingHardwareParts
  );

  console.log("ManagerMyProjects ongoingWorkentries", ongoingWorkentries);
  console.log("ManagerMyProjects ongoingProjectsRedux", ongoingProjectsRedux);

  const [probabilityTab, setProbabilityTab] = useState("graph");
  const [approvedHours, SetApprovedHours] = useState("graph");
  const [showLogout, setshowlogout] = useState(false);
  const [showList, setShowList] = useState(false);
  const { employees } = useSelector((state) => state.employees);

  const [selectedData, setSelectedData] = useState(null); // 'weekly' or 'monthly'

  const mytrainingOMMyManagement = useSelector(
    (state) => state.operationState.mytrainingOMMyManagement
  );

  const ongoingEntries = useMemo(() => {
    const entries = Array.isArray(ongoingWorkentries) ? ongoingWorkentries : [];
    // Prevent flickering by ensuring we only use stable data
    return entries;
  }, [ongoingWorkentries]);

  const projects = useMemo(() => {
    return Array.isArray(ongoingProjectsRedux) ? ongoingProjectsRedux : [];
  }, [ongoingProjectsRedux]);

  const [selectedEmp] = useState(null);

  useEffect(() => {
    dispatch(fetchAllProjects());
    dispatch(fetchWorkEntries());
    dispatch(fetchEmployees());
    dispatch(fetchWorkEntriesProjectTracking());
    dispatch(setmytrainingOMProjectRequestState());
    dispatch(setmytrainingOMProjectAssignedState());
  }, [dispatch]);


  const [currentDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const [, setEmpEntriesData] = useState({});

  useEffect(() => {
    // if (!ongoingEntries?.length) return;
    let entriesToUse = ongoingEntries;
    if (currentDate) {
      const startOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      const endOfMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );

      entriesToUse = ongoingEntries?.filter((entry) => {
        const start = new Date(entry?.startdate);
        return start >= startOfMonth && start <= endOfMonth;
      });
    }
    const getWeekNumberInMonth = (date) => {
      const day = date.getDate();
      const firstDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        1
      ).getDay(); // 0 = Sun
      return Math.ceil((day + firstDay) / 7);
    };
    const calculateWeeklyAssigned = (entries) => {
      const weeklyMap = {};
      entries.forEach((entry) => {
        const start = new Date(entry.startdate);
        const weekNum = getWeekNumberInMonth(start);

        if (!weeklyMap[weekNum]) weeklyMap[weekNum] = 0;
        weeklyMap[weekNum] += entry.hoursassigned || 0;
      });

      return weeklyMap;
    };
    const calculateEmpData = (emp, entries) => {
      const matchedEntries = entries?.filter(
        (entry) => entry?.taskownerid === emp._id
      );
      const AssignedHrs = matchedEntries.reduce(
        (acc, cur) => acc + (cur?.hoursassigned || 0),
        0
      );
      const ApprovedHrs = matchedEntries.reduce(
        (acc, cur) => acc + (cur?.hoursspent || 0),
        0
      );
      const RemainingHrs = AssignedHrs - ApprovedHrs;
      const weeklyAssigned = calculateWeeklyAssigned(matchedEntries);
      return {
        empName: `${emp.EmpFirstName || ""} ${emp.EmpLastName || ""}`.trim(),
        empId: emp._id,
        totalEntries: matchedEntries,
        AssignedHrs,
        ApprovedHrs,
        RemainingHrs,
        weeklyAssigned,
      };
    };
    let result;
    if (selectedEmp?._id) {
      result = calculateEmpData(selectedEmp, entriesToUse);
    } else {
      const AssignedHrs = entriesToUse.reduce(
        (acc, cur) => acc + (cur.hoursassigned || 0),
        0
      );
      const ApprovedHrs = entriesToUse.reduce(
        (acc, cur) => acc + (cur.hoursspent || 0),
        0
      );
      const RemainingHrs = AssignedHrs - ApprovedHrs;
      const weeklyAssigned = calculateWeeklyAssigned(entriesToUse);
      result = {
        empName: "All",
        empId: "allId",
        totalEntries: entriesToUse,
        AssignedHrs,
        ApprovedHrs,
        RemainingHrs,
        weeklyAssigned, // ✅ dynamic week-wise hours
      };
    }
    setEmpEntriesData(result);
  }, [ongoingEntries, employees, selectedEmp, currentDate]);

  // const [oldestDate, setOldestDate] = useState(null);

  // useEffect(() => {
  //     if (!ongoingEntries?.length) return;
  //     const validEntries = ongoingEntries.filter(
  //         (entry) => entry.startdate && !isNaN(new Date(entry.startdate))
  //     );
  //     if (!validEntries.length) return; // no valid dates → skip
  //     const oldest = validEntries.reduce((min, entry) => {
  //         const entryDate = new Date(entry.startdate);
  //         return entryDate < min ? entryDate : min;
  //     }, new Date(validEntries[0].startdate));
  //     const monthStart = new Date(oldest.getFullYear(), oldest.getMonth(), 1);
  //     setOldestDate(monthStart);
  // }, [ongoingEntries]);

  const [weeklyLeadCount, setWeeklyLeadCount] = useState([]);
  const [monthlyLeadCount, setMonthlyLeadCount] = useState([]);

  // Use same data source and filtering as Approved Hrs (ongoingEntries) for My Projects.
  // For hardware service type: also include project IDs from ongoingHardwareParts so that
  // "Project Ongoing" projects that have only hardware parts (no work entries) still show in My Projects.
  useEffect(() => {
    const today = new Date();

    // Week: Monday to Sunday (calendar week, ISO)
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday, 0, 0, 0, 0);
    const endOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday + 6, 23, 59, 59, 999);

    // Month: 1st to last day of current calendar month
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

    const entries = Array.isArray(ongoingEntries) ? ongoingEntries : [];
    const hardwareParts = Array.isArray(ongoingHardwareParts) ? ongoingHardwareParts : [];

    const weeklyEntries = entries.filter((entry) => {
      if (!entry?.startdate) return false;
      const startDate = new Date(entry.startdate);
      return startDate >= startOfWeek && startDate <= endOfWeek;
    });

    const monthlyEntries = entries.filter((entry) => {
      if (!entry?.startdate) return false;
      const startDate = new Date(entry.startdate);
      return startDate >= startOfMonth && startDate <= endOfMonth;
    });

    // Hardware: project IDs from hardware parts (use startdate for week/month; if no startdate, include in both so project still shows)
    const weeklyHardwareProjectIds = hardwareParts
      .filter((e) => {
        if (!e?.projectid) return false;
        if (!e?.startdate) return true;
        const d = new Date(e.startdate);
        return d >= startOfWeek && d <= endOfWeek;
      })
      .map((e) => e.projectid);
    const monthlyHardwareProjectIds = hardwareParts
      .filter((e) => {
        if (!e?.projectid) return false;
        if (!e?.startdate) return true;
        const d = new Date(e.startdate);
        return d >= startOfMonth && d <= endOfMonth;
      })
      .map((e) => e.projectid);

    // Get unique project IDs: from work entries + from hardware parts (for hardware-only projects)
    const weeklyProjectIds = [
      ...new Set([
        ...weeklyEntries.map((e) => e.projectid).filter(Boolean),
        ...weeklyHardwareProjectIds,
      ]),
    ];
    const monthlyProjectIds = [
      ...new Set([
        ...monthlyEntries.map((e) => e.projectid).filter(Boolean),
        ...monthlyHardwareProjectIds,
      ]),
    ];

    // Match with projects for list view (project names)
    const weeklyProjects =
      projects?.filter((p) => weeklyProjectIds.includes(p._id)) ?? [];
    const monthlyProjects =
      projects?.filter((p) => monthlyProjectIds.includes(p._id)) ?? [];

    setWeeklyLeadCount(weeklyProjects);
    setMonthlyLeadCount(monthlyProjects);
  }, [ongoingEntries, ongoingHardwareParts, projects]);

  const data = [
    { name: "Week", value: weeklyLeadCount.length },
    { name: "Month", value: monthlyLeadCount.length },
  ];
  const CustomTooltips = ({ active, payload, label, coordinate }) => {
    // Only show tooltip when hovering directly on a bar
    // Check that we have active state, valid payload, coordinate exists, and value is defined
    if (!active || !payload || payload.length === 0 || !coordinate || payload[0]?.value === undefined) {
      return null;
    }

    // Use payload's name (Week/Month) for the hovered bar - label can be wrong for multiple bars
    const barLabel = payload[0]?.payload?.name ?? label;

    return (
      <div style={{
        backgroundColor: '#ffffff',
        color: 'black',
        padding: '5px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        pointerEvents: 'none',
      }}>
        <p style={{ margin: 0 }}>{`${barLabel}: ${payload[0].value} ${payload[0].value === 1 ? 'Project' : 'Projects'} `}</p>
      </div>
    );
  };
  console.log("sfsdgsdg", data)

  const [ApprovedHoursData, setApprovedHoursData] = useState([
    { name: "Week", approved: 0, assigned: 0 },
    { name: "Month", approved: 0, assigned: 0 },
  ]);

  useEffect(() => {
    // Prevent unnecessary updates and flickering
    if (!ongoingEntries || ongoingEntries.length === 0) {
      setApprovedHoursData([
        { name: "Week", approved: 0, assigned: 0 },
        { name: "Month", approved: 0, assigned: 0 },
      ]);
      return;
    }

    const today = new Date();

    // Week: Monday to Sunday (calendar week, ISO)
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday, 0, 0, 0, 0);
    const endOfWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - daysSinceMonday + 6, 23, 59, 59, 999);

    // Month: 1st to last day of current calendar month
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

    const weeklyEntries = ongoingEntries.filter((entry) => {
      if (!entry?.startdate) return false;
      const startDate = new Date(entry.startdate);
      return startDate >= startOfWeek && startDate <= endOfWeek;
    });

    const monthlyEntries = ongoingEntries.filter((entry) => {
      if (!entry?.startdate) return false;
      const startDate = new Date(entry.startdate);
      return startDate >= startOfMonth && startDate <= endOfMonth;
    });

    // Calculate totals - Matching Operations Manager calculateEmpData formula
    // Assigned Hours = hoursassigned
    // Approved Hours = hoursspent
    const weekAssignedCount = weeklyEntries.reduce(
      (sum, entry) => sum + (Number(entry.hoursassigned) || 0),
      0
    );
    const weekApprovedCount = weeklyEntries.reduce(
      (sum, entry) => sum + (Number(entry.hoursspent) || 0),
      0
    );

    const monthlyAssignedCount = monthlyEntries.reduce(
      (sum, entry) => sum + (Number(entry.hoursassigned) || 0),
      0
    );
    const monthlyApprovedCount = monthlyEntries.reduce(
      (sum, entry) => sum + (Number(entry.hoursspent) || 0),
      0
    );

    // Only update state if values have actually changed to prevent flickering
    setApprovedHoursData((prevData) => {
      const newData = [
        {
          name: "Week",
          approved: weekApprovedCount,
          assigned: weekAssignedCount,
        },
        {
          name: "Month",
          approved: monthlyApprovedCount,
          assigned: monthlyAssignedCount,
        },
      ];
      
      // Check if values actually changed to prevent unnecessary re-renders and flickering
      if (prevData && prevData.length === 2) {
        const hasChanged = 
          prevData[0]?.approved !== newData[0].approved ||
          prevData[0]?.assigned !== newData[0].assigned ||
          prevData[1]?.approved !== newData[1].approved ||
          prevData[1]?.assigned !== newData[1].assigned;
        
        if (!hasChanged) {
          return prevData; // Return previous data to prevent re-render
        }
      }
      
      return newData;
    });
  }, [ongoingEntries]);

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    // Only show tooltip when hovering directly on a bar
    // Check that we have active state, valid payload, and coordinate exists (means we're on a chart element)
    if (!active || !payload || payload.length === 0 || !coordinate) {
      return null;
    }

    // For stacked bars, payload contains both bars, find their values
    const assignedBar = payload.find((p) => p.dataKey === "assigned");
    const approvedBar = payload.find((p) => p.dataKey === "approved");
    
    const assigned = assignedBar?.value ?? assignedBar?.payload?.assigned ?? 0;
    const approved = approvedBar?.value ?? approvedBar?.payload?.approved ?? 0;

    // Additional check: ensure we're actually hovering over a bar by checking if payload has valid dataKey
    // If we're hovering outside bars, payload might be empty or have invalid data
    const hasValidBarData = assignedBar || approvedBar;
    
    if (!hasValidBarData) {
      return null;
    }

    // Show tooltip if we have valid data
    return (
      <div className="bg-white p-2 border border-gray-300 rounded-md shadow-md text-xs" style={{ pointerEvents: 'none' }}>
        <p className="font-semibold text-gray-700">{label}</p>
        <p className="text-blue-600">Assigned: {formatHours(assigned)} hrs</p>
        <p className="text-[#14597a]">Approved: {formatHours(approved)} hrs</p>
      </div>
    );
  };

  const handleClickOutside = (event) => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setshowlogout(false); // Close the popup if clicked outside
    }
  };

  useEffect(() => {
    if (showLogout) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLogout]);

  // const handlePrev = () => {
  //     setCurrentDate((prev) => {
  //         const newDate = new Date(prev?.getFullYear(), prev?.getMonth() - 1, 1);
  //         return newDate >= new Date(2023, 0, 1) ? newDate : prev;
  //     });
  // };

  // const handleNext = () => {
  //     setCurrentDate((prev) => {
  //         const maxDate = new Date();
  //         maxDate.setFullYear(maxDate?.getFullYear() + 1);
  //         const newDate = new Date(prev?.getFullYear(), prev?.getMonth() + 1, 1);
  //         return newDate <= maxDate ? newDate : prev;
  //     });
  // };

  // const handleSelectChange = (e) => {
  //     const selected = new Date(e.target.value);
  //     setCurrentDate(selected);
  // };
  // const monthOptions = useMemo(() => {
  //     if (!oldestDate) return []; // wait until oldestDate is available
  //     const start = new Date(oldestDate); // 👈 use oldestDate as starting month
  //     const end = new Date(); // current date
  //     end.setFullYear(end.getFullYear() + 1); // allow 1 year ahead
  //     const options = [];
  //     let date = new Date(start);
  //     while (date <= end) {
  //         options.push({
  //             label: date.toLocaleString("default", {
  //                 month: "long",
  //                 year: "numeric",
  //             }),
  //             value: new Date(date),
  //         });
  //         date.setMonth(date.getMonth() + 1);
  //     }
  //     return options;
  // }, [oldestDate]);

  // const year = currentDate?.getFullYear();
  // const month = currentDate?.getMonth();

  // const firstDay = new Date(year, month, 1);
  // const lastDay = new Date(year, month + 1, 0);

  // const getWeeksInMonth = () => {
  //     const weeks = [];
  //     let currentWeek = [];
  //     for (let i = 0; i < firstDay.getDay(); i++) {
  //         currentWeek.push(null);
  //     }
  //     for (let day = 1; day <= lastDay.getDate(); day++) {
  //         currentWeek.push(day);
  //         if (currentWeek.length === 7) {
  //             weeks.push(currentWeek);
  //             currentWeek = [];
  //         }
  //     }
  //     if (currentWeek.length > 0) {
  //         while (currentWeek.length < 7) currentWeek.push(null);
  //         weeks.push(currentWeek);
  //     }
  //     return weeks;
  // };

  // const weeks = getWeeksInMonth();

  // const getColor = (val) => {
  //     if (val > 40) return "bg-red-300 text-red-900";
  //     if (val > 10) return "bg-yellow-200 text-yellow-900";
  //     return "bg-green-200 text-green-900";
  // };

  const chartHeight =
    mytrainingOMMyManagement === "Assignment" ||
      (loggedUser?.EmpAuthLevel === "L4" &&
        loggedUser?.customRoles?.includes("operations"))
      ? 260
      : 270;

  return (
    <>
      <div className=" bg-[#f3f8f8] h-full">
        <div className="flex gap-1 w-full h-full">
          {canShowCard("MY PROJECTS") && (
            <div
              className={`px-1 relative pb-3 bg-white rounded-lg ${
                loggedUser?.servicetype?.toLowerCase() === "hardware"
                  ? "w-full"
                  : canShowCard("MY PROJECTS") &&
                    !canShowCard("APPROVED HRS")
                    ? "w-full"
                    : canShowCard("MY PROJECTS") &&
                      canShowCard("APPROVED HRS")
                      ? "w-1/2"
                      : ""
                } justify-center h-full`}
              style={{ fontFamily: "Lora", fontWeight: 600 }}
            >
              <div className="h-full">
                <div className="h-[10%]">
                  <div className="relative p-1 flex flex-wrap justify-start items-center xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
                    <div>
                      <h3 className=" font-semibold ">My Projects</h3>
                    </div>
                    <div className="pr-2 md:flex lg:ml-8 lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end absolute right-0">
                      <div className="show-potential-download lg:justify-end">
                        <div className=" flex items-center justify-between text-3xl sm:text-xl md:text-md">
                          {probabilityTab === "table" && (
                            <span title="Graph">
                              <SignalCellularAltIcon
                                className="cursor-pointer"
                                onClick={() => setProbabilityTab("graph")}
                                fontSize="3rem"
                              />
                            </span>
                          )}
                          {probabilityTab === "graph" && (
                            <span title="Table">
                              <TableChartIcon
                                className="cursor-pointer"
                                onClick={() => setProbabilityTab("table")}
                                fontSize="3rem"
                              />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <hr />
                <div className="h-[90%] flex">
                  {probabilityTab === "table" ? (
                    data.length > 0 ? (
                      <div className="w-full ">
                        <div className="overflow-auto border text-xs">
                          <div className="w-full flex ">
                            <div className="w-[50%] text-center bg-[#e8e8e8]  border-b px-4 py-3">
                              <p className=" text-xs">Week</p>
                            </div>
                            <div className="w-[50%] text-center bg-[#e8e8e8] border-b px-4 py-3">
                              <p className=" text-xs">Month</p>
                            </div>
                          </div>

                          <div className="max-h-[17rem] overflow-y-auto">
                            <div className="w-full flex">
                              <div className="w-[50%] text-center flex flex-col  border ">
                                {weeklyLeadCount && weeklyLeadCount.length > 0 ? (
                                  weeklyLeadCount.map((item, index) => (
                                    <div
                                      key={item._id || index}
                                      className=" bg-white flex border-b w-full"
                                    >

                                      <div className="flex w-full justify-center   align-middle items-center p-2">
                                        {item.ProjectName}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-center text-red-400">
                                    No Projects
                                  </p>
                                )}
                              </div>
                              <div className="w-[50%] text-center border flex flex-col">
                                {monthlyLeadCount && monthlyLeadCount.length > 0 ? (
                                  monthlyLeadCount.map((item, index) => (
                                    <div
                                      key={item._id || index}
                                      className=" bg-white flex border-b w-full"
                                    >
                                      <div className="flex border-b w-full justify-center align-middle items-center p-2">
                                        {item.ProjectName}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-center text-red-400">
                                    No Projects
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                        <p className="noDataAvailable">No data available</p>{" "}
                      </div>
                    )
                  ) : data.length > 0 ? (
                    <div className="w-full flex justify-center items-center ">
                      {!showList ? (
                        <>
                          <div
                            className="flex flex-col justify-end"
                            style={{ width: 170, height: 300 }}
                          >
                            <BarChart
                              width={170}
                              height={250}
                              data={data}
                              margin={{
                                top: 10,
                                right: 10,
                                left: 10,
                                bottom: 10,
                              }}
                            >
                              <XAxis
                                dataKey="name"
                                height={60}
                                tick={{ fontSize: 10 }}
                              />
                              <Bar
                                dataKey="value"
                                fill="#14597a"
                                barSize={30}
                                isAnimationActive={false}
                                activeBar={{ fillOpacity: 1 }}
                                cursor="pointer"
                                onClick={(barData, index) => {
                                  const payload = barData?.payload ?? barData;
                                  const barName = payload?.name;
                                  if (barName === 'Week') {
                                    setSelectedData('weekly');
                                    setShowList(true);
                                  } else if (barName === 'Month') {
                                    setSelectedData('monthly');
                                    setShowList(true);
                                  }
                                }}
                              />
                              <Tooltip
                                content={<CustomTooltips />}
                                cursor={false}
                                shared={false}
                                filterNull={true}
                                trigger="hover"
                                allowEscapeViewBox={{ x: false, y: false }}
                              />
                            </BarChart>
                            <p className="flex justify-center cursor-pointer items-center text-center text-xs -mt-8">
                              My Projects
                            </p>
                          </div>
                        </>
                      ) : (
                        <div className="w-full h-full">
                          <div className="flex justify-between items-center p-2">
                            <div className="text-sm font-semibold">
                              {selectedData === 'weekly' ? 'Weekly Projects' : 'Monthly Projects'}
                            </div>
                            <CloseIcon
                              className="cursor-pointer"
                              onClick={() => {
                                setShowList(false);
                                setSelectedData(null);
                              }}
                              fontSize="3rem"
                            />
                          </div>
                          <div className="w-full h-[80%] overflow-y-auto   text-xs">
                            <div className="bg-[#e8e8e8] flex items-center">
                              <div className="w-[30%] border-b flex justify-center align-middle items-center p-2">
                                Sr. No
                              </div>
                              <div className="w-[70%] border-b flex justify-center align-middle items-center p-2">
                                Project Name
                              </div>
                            </div>
                            <div className="h-[80%] overflow-y-auto">
                              {selectedData === 'weekly' ? (
                                weeklyLeadCount && weeklyLeadCount.length > 0 ? (
                                  weeklyLeadCount.map((item, index) => (
                                    <div
                                      key={item._id || index}
                                      className="border bg-white flex"
                                    >
                                      <div className="w-[30%] border-b flex justify-center align-middle items-center p-2">
                                        {index + 1}
                                      </div>
                                      <div className="w-[70%] border-b flex justify-center align-middle items-center p-2">
                                        {item.ProjectName}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-center text-red-400 p-4">
                                    No Weekly Projects
                                  </p>
                                )
                              ) : selectedData === 'monthly' ? (
                                monthlyLeadCount && monthlyLeadCount.length > 0 ? (
                                  monthlyLeadCount.map((item, index) => (
                                    <div
                                      key={item._id || index}
                                      className="border bg-white flex"
                                    >
                                      <div className="w-[30%] border-b flex justify-center align-middle items-center p-2">
                                        {index + 1}
                                      </div>
                                      <div className="w-[70%] border-b flex justify-center align-middle items-center p-2">
                                        {item.ProjectName}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-center text-red-400 p-4">
                                    No Monthly Projects
                                  </p>
                                )
                              ) : (
                                <p className="text-center text-red-400 p-4">
                                  No data selected
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex justify-center align-middle items-center text-center mb-5  w-full">
                      <p className="noDataAvailable">No data available</p>{" "}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {canShowCard("APPROVED HRS") && 
           loggedUser?.servicetype?.toLowerCase() !== "hardware" && (
            <div
              className={`bg-white rounded-lg ${!canShowCard("MY PROJECTS") &&
                canShowCard("APPROVED HRS")
                ? "w-full"
                : canShowCard("MY PROJECTS") &&
                  canShowCard("APPROVED HRS")
                  ? "w-1/2"
                  : ""
                } justify-center h-full `}
              style={{ fontFamily: "Lora", fontWeight: 600 }}
            >
              <div className="h-[10%] ">
                <div className="relative p-1 flex flex-wrap justify-between items-center xl:flex xl:justify-between md:justify-between md:flex-nowrap ">
                  <div>
                    <h3 className="font-semibold">Approved Hrs</h3>
                  </div>

                  <div className="flex gap-1">
                    <div className="pr-2 md:flex lg:flex lg:items-center lg:justify-end xl:flex xl:justify-end">
                      <div className="show-potential-download lg:justify-end">
                        <div className=" flex items-center justify-between text-3xl sm:text-xl md:text-md">
                          {approvedHours === "table" && (
                            <span title="Graph">
                              <SignalCellularAltIcon
                                className="cursor-pointer"
                                onClick={() => SetApprovedHours("graph")}
                                fontSize="3rem"
                              />
                            </span>
                          )}
                          {approvedHours === "graph" && (
                            <span title="Table">
                              <TableChartIcon
                                className="cursor-pointer"
                                onClick={() => SetApprovedHours("table")}
                                fontSize="3rem"
                              />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
              <hr />

              <div className={`h-[90%]`}>
                {approvedHours === "table" ? (
                  ApprovedHoursData.length > 0 ? ( // Changed from data.length to ApprovedHoursData.length
                    <div>
                      <div
                        className={`w-full text-xs ${mytrainingOMMyManagement === "Assignment" ||
                          (loggedUser?.EmpAuthLevel === "L4" &&
                            loggedUser?.customRoles?.includes("operations"))
                          ? ""
                          : " "
                          }  flex border flex-col bg-white`}
                      >
                        {/* Table Header */}
                        <div className="grid grid-cols-3 text-center text-xs border-b border-gray-200">
                          <p className="bg-[#e8e8e8] border-b p-1 py-3">

                          </p>
                          <p className="bg-[#e8e8e8] border-b p-1 py-3">
                            Assigned Hrs
                          </p>
                          <p className="bg-[#e8e8e8] border-b p-1 py-3">
                            Approved Hrs
                          </p>
                        </div>

                        {/* Render rows from ApprovedHoursData */}
                        {ApprovedHoursData.map((item, index) => (
                          <div
                            key={item.name || index}
                            className={`grid grid-cols-3 text-center ${index < ApprovedHoursData.length - 1 ? 'border-b border-gray-200' : ''}`}
                          >
                            <p className="font-semibold text-black p-1 py-3 capitalize">
                              {item.name || (index === 0 ? 'Weekly' : 'Monthly')}
                            </p>
                            <p className="font-semibold text-black p-1 py-3">
                              {formatHours(item.assigned ?? item.assignedHrs ?? 0)}

                            </p>
                            <p className="font-semibold text-black p-1 py-3">
                              {/* {item.assigned ?? item.assignedHrs ?? 0} */}
                              {formatHours(item.approved ?? item.approvedHrs ?? 0)}

                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-center align-middle items-center text-center mb-5 w-full">
                      <p className="noDataAvailable">No data available</p>
                    </div>
                  )
                ) : ApprovedHoursData.length > 0 ? (
                  // Graph view remains the same
                  <div className="w-full flex justify-center items-center">
                    <div
                      className="flex flex-col justify-end"
                      style={{ width: 170, height: 300 }}
                    >
                      <BarChart
                        width={170}
                        height={chartHeight}
                        data={ApprovedHoursData}
                        margin={{ top: 5, right: 10, left: 10, bottom: 10 }}
                      >
                        <XAxis
                          dataKey="name"
                          height={60}
                          tick={{ fontSize: 10 }}
                        />
                        <Tooltip
                          content={<CustomTooltip />}
                          cursor={false}
                          shared={true}
                          filterNull={false}
                          trigger="hover"
                          allowEscapeViewBox={{ x: false, y: false }}
                        />
                        <Bar
                          dataKey="approved"
                          stackId="a"
                          fill="#14597a"
                          barSize={30}
                          isAnimationActive={false}
                          activeBar={{ fillOpacity: 1 }}
                        />
                        <Bar
                          dataKey="assigned"
                          stackId="a"
                          fill="#00bcd4"
                          barSize={30}
                          isAnimationActive={false}
                          activeBar={{ fillOpacity: 1 }}
                        />
                      </BarChart>
                      <p className="flex justify-center cursor-pointer items-center text-center text-xs -mt-8">
                        Approved Hrs
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center align-middle items-center text-center mb-5 w-full">
                    <p className="noDataAvailable">No data available</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ManagerMyProjects;
